/**
 * Profile tab (route #/settings): who you are, your credits, shortcuts to your
 * creations / daily reward / credit history, and account actions.
 *
 * Adapted from Graphic/Profile design. Not shown on purpose: Followers /
 * Following / Collections folders (no social features or folders exist yet).
 */
import type { AiJob, CreditTx, UploadFileMeta } from '@loomia/shared';
import { api } from '../api/client.js';
import { getUser, hasAccount } from '../state/auth.js';
import { getPrefs } from '../state/prefs.js';
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { toastApiError } from '../ui/toast.js';
import { openDailyReward } from '../ui/dailyReward.js';
import { escapeHtml } from '../components/placeholder.js';
import { icon } from '../components/icon.js';
import { openSignInSheet } from '../ui/signinSheet.js';

const RECENT = 4;

export function displayName(email: string | undefined): string {
  const custom = getPrefs().displayName.trim();
  if (custom) return custom;
  const local = (email ?? '').split('@')[0] ?? '';
  const clean = local.replace(/[._-]+/g, ' ').trim();
  return clean ? clean.replace(/\b\w/g, (c) => c.toUpperCase()) : 'Creator';
}

function menuItem(ic: string, title: string, sub: string, attrs: string): string {
  return `
    <a class="pf-item" ${attrs}>
      <span class="pf-item-ic">${icon(ic)}</span>
      <span class="pf-item-tx"><strong>${title}</strong><small>${sub}</small></span>
      ${icon('chevron_right')}
    </a>`;
}

export function renderProfile(root: HTMLElement): void {
  if (!hasAccount()) {
    root.innerHTML = `
      <div class="profile">
        <div class="empty">
          <div class="pf-avatar">${icon('person')}</div>
          <h1 class="page-title">Your profile</h1>
          <p class="muted" style="max-width: 280px;">Sign in to keep your creations, credits and daily rewards across devices.</p>
          <button class="btn btn-primary" id="pf-signin" type="button">Sign in</button>
        </div>
      </div>`;
    root.querySelector('#pf-signin')?.addEventListener('click', () => openSignInSheet('Sign in to see your profile.'));
    return;
  }

  const user = getUser();
  const email = user?.email ?? '';
  root.innerHTML = `
    <div class="profile">
      <a class="pf-gear" href="#/settings" aria-label="Settings">${icon('settings')}</a>
      <header class="pf-head">
        <div class="pf-avatar" aria-hidden="true">${escapeHtml(displayName(email).charAt(0))}</div>
        <div>
          <h1 class="pf-name">${escapeHtml(displayName(email))}</h1>
          <p class="pf-email">${escapeHtml(email)}</p>
          <p class="pf-tag">Create more. Be more. ✨</p>
        </div>
      </header>

      <div class="pf-stats" aria-label="Your stats">
        <div><b id="pf-n-creations">–</b><span>Creations</span></div>
        <div><b id="pf-n-streak">–</b><span>Day streak</span></div>
        <div><b id="pf-n-credits">–</b><span>Credits ✦</span></div>
      </div>

      <section class="pf-credits" aria-label="Credits">
        <div>
          <small>Your Credits</small>
          <strong id="pf-credits">–</strong>
        </div>
        <a class="pf-recharge" href="#/paywall">Recharge ${icon('chevron_right')}</a>
      </section>

      <nav class="pf-menu" aria-label="Profile">
        ${menuItem('imagesmode', 'My Creations', 'View and manage your AI creations', 'href="#/collection"')}
        ${menuItem('redeem', 'Daily Reward', 'Check in every day for free credits', 'href="#/profile" data-act="daily"')}
        ${menuItem('history', 'Credit History', 'View your transactions and usage', 'href="#/profile" data-act="history"')}
        ${menuItem('settings', 'Settings', 'Account, notifications, preferences', 'href="#/settings"')}
        ${menuItem('help', 'Help & Support', 'FAQs, contact us', 'href="#/settings/help"')}
      </nav>

      <section aria-label="Recent creations">
        <div class="rail-head">
          <h2 class="section-title">Recent Creations</h2>
          <a class="see-all" href="#/collection">View all ${icon('arrow_forward')}</a>
        </div>
        <div class="pf-recent" id="pf-recent"></div>
      </section>

    </div>`;

  const setCredits = (n: number | null): void => {
    const t = n === null ? '–' : n.toLocaleString();
    root.querySelector('#pf-credits')!.textContent = t;
    root.querySelector('#pf-n-credits')!.textContent = t;
  };
  setCredits(getBalance());
  const off = creditsChanged.on(() => {
    if (!root.isConnected) off();
    else setCredits(getBalance());
  });
  void refreshCredits().then(setCredits).catch(() => undefined);

  void api
    .getDailyReward()
    .then((d) => {
      root.querySelector('#pf-n-streak')!.textContent = String(d.claimedDays);
    })
    .catch(() => undefined);

  void loadRecent(root);

  root.querySelector('[data-act="daily"]')?.addEventListener('click', (e) => {
    e.preventDefault();
    void openDailyReward();
  });
  root.querySelector('[data-act="history"]')?.addEventListener('click', (e) => {
    e.preventDefault();
    void openHistory();
  });
}

async function loadRecent(root: HTMLElement): Promise<void> {
  const host = root.querySelector<HTMLElement>('#pf-recent');
  const count = root.querySelector<HTMLElement>('#pf-n-creations');
  if (!host) return;
  try {
    const page = await api.listJobs({ limit: 50 });
    const done: AiJob[] = page.jobs.filter((j) => j.state === 'done' && j.resultFileId);
    if (count) count.textContent = page.nextCursor ? `${done.length}+` : String(done.length);
    if (done.length === 0) {
      host.innerHTML = `<p class="muted pf-empty">Nothing here yet. <a href="#/create">Create your first</a>.</p>`;
      return;
    }
    const metas = await Promise.allSettled(done.slice(0, RECENT).map((j) => api.getUpload(j.resultFileId as string)));
    host.innerHTML = metas
      .map((r, i) => {
        if (r.status !== 'fulfilled') return '';
        const m: UploadFileMeta = r.value;
        const src = m.thumbnails?.thumb256 ?? m.url ?? '';
        if (!src) return '';
        const video = (m.contentType ?? '').startsWith('video/');
        return `
          <a class="pf-thumb" href="#/result/${encodeURIComponent(done[i]!.id)}" aria-label="Open creation">
            <img src="${escapeHtml(src)}" alt="" loading="lazy" decoding="async" />
            ${video ? `<span class="pf-play">${icon('play_arrow', { fill: true })}</span>` : ''}
          </a>`;
      })
      .join('');
  } catch {
    if (count) count.textContent = '–';
    host.innerHTML = `<p class="muted pf-empty">Couldn't load your creations.</p>`;
  }
}

const TX_LABEL: Record<string, string> = {
  daily_reward: 'Daily reward',
  signup_bonus: 'Welcome bonus',
  purchase: 'Credit pack',
  subscription: 'Subscription',
  admin_adjust: 'Adjustment',
};

function txTitle(t: CreditTx): string {
  if (t.type === 'spend') return t.tool ? `Used · ${t.tool.replace(/_/g, ' ')}` : 'Used';
  if (t.type === 'refund') return 'Refund';
  if (t.type === 'expire') return 'Expired';
  if (t.type === 'hold') return 'Reserved for a job';
  if (t.type === 'hold_release') return 'Released';
  return TX_LABEL[t.reason] ?? 'Credits added';
}

async function openHistory(): Promise<void> {
  document.getElementById('history-sheet')?.remove();
  const el = document.createElement('div');
  el.id = 'history-sheet';
  el.className = 'sheet-backdrop';
  el.innerHTML = `
    <div class="sheet" role="dialog" aria-modal="true" aria-label="Credit history">
      <div class="sheet-handle"></div>
      <h2 class="page-title" style="font-size: var(--fs-xl);">Credit History</h2>
      <div class="pf-tx" id="pf-tx"><p class="muted">Loading…</p></div>
    </div>`;
  document.body.appendChild(el);
  const close = (): void => {
    el.classList.remove('open');
    window.setTimeout(() => el.remove(), 200);
  };
  el.addEventListener('click', (e) => {
    if (e.target === el) close();
  });
  requestAnimationFrame(() => el.classList.add('open'));

  const list = el.querySelector<HTMLElement>('#pf-tx')!;
  try {
    const { transactions } = await api.getCreditHistory(30);
    const shown = transactions.filter((t) => t.type !== 'hold' && t.type !== 'hold_release');
    list.innerHTML = shown.length
      ? shown
          .map((t) => {
            const plus = t.type === 'grant' || t.type === 'refund';
            return `
            <div class="pf-tx-row">
              <span><strong>${escapeHtml(txTitle(t))}</strong><small>${new Date(t.createdAt).toLocaleString()}</small></span>
              <b class="${plus ? 'pf-plus' : 'pf-minus'}">${plus ? '+' : '−'}${t.amount} ✦</b>
            </div>`;
          })
          .join('')
      : '<p class="muted">No transactions yet.</p>';
  } catch (err) {
    list.innerHTML = '<p class="muted">Couldn\'t load your history.</p>';
    toastApiError(err);
  }
}
